import { ChangeDetectionStrategy, Component, computed, forwardRef, input, output, signal } from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { MaskitoOptions } from '@maskito/core';
import { MaskitoDirective } from '@maskito/angular';
import { InputSize } from '../../../enum/input-size.enum';
import { PostalCodeUtils } from '../../../../utils/postal-code-utils';
import { CountryType } from '../../../../../core';

@Component({
  selector: 'lib-postal-code-input',
  templateUrl: './postal-code-input.component.html',
  styleUrl: './postal-code-input.component.scss',
  imports: [MaskitoDirective],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => PostalCodeInputComponent),
      multi: true,
    },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PostalCodeInputComponent implements ControlValueAccessor {
  countryType = input.required<CountryType>();
  placeholder = input<string>('');
  controlId = input<string | null>(null);
  error = input<boolean>(false);
  inputSize = input<InputSize>(InputSize.large);
  valueChange = output<string | null>();

  protected readonly InputSize = InputSize;

  public value = signal<string | null>(null);
  public disabled = signal(false);

  protected readonly maskitoOptions = computed<MaskitoOptions>(() =>
    PostalCodeUtils.getMaskitoOptions(this.countryType()),
  );

  public writeValue(value: string | null): void {
    if (!value) {
      this.value.set(null);
      return;
    }
    const normalized = PostalCodeUtils.normalize(value, this.countryType());
    this.value.set(normalized || null);
  }

  public registerOnChange(fn: (value: string | null) => void): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  public handleInput(event: Event): void {
    const inputValue = (event.target as HTMLInputElement).value || null;
    this.value.set(inputValue);
    this.valueChange.emit(inputValue);
    this.onChange(inputValue);
  }

  public setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }

  private onChange: (value: string | null) => void = () => {
    // default onChange
  };
  private onTouched: () => void = () => {
    // default onTouched
  };
}
